import React, { useMemo } from 'react';
import { Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ChatAvatar, type ChatParticipant } from '../../../components/chat';
import { useTranslation } from '../../../i18n';
import type { EmailConversationStatus } from '../../../types/email';
import { formatInboxTime } from '../../../utils/timeFormat';
import { emailStatusTag } from './emailTags';

export type EmailListFilter = 'all' | EmailConversationStatus;

export type EmailListRow = {
  id: string;
  name: string;
  email: string;
  handle: string;
  avatarUrl?: string;
  subject: string;
  status?: string;
  unreadCount: number;
  preview: string;
  time?: string;
  direction?: string;
  sentCount: number;
  peer: ChatParticipant;
};

type EmailConversationListProps = {
  conversations: EmailListRow[];
  activeId?: string;
  filter: EmailListFilter;
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  loading: boolean;
  loadingMore?: boolean;
  emptyText: string;
  pendingReplyCount: number | null;
  onLoadMore?: () => void;
  onFilterChange: (filter: EmailListFilter) => void;
  onSelect: (id: string) => void;
};

function formatEmailTime(value?: string) {
  if (!value) return '';
  const day = dayjs(value);
  if (!day.isValid()) return value;
  return formatInboxTime(value);
}

export function EmailConversationList({
  conversations,
  activeId,
  filter,
  keyword,
  onKeywordChange,
  loading,
  loadingMore,
  emptyText,
  pendingReplyCount,
  onLoadMore,
  onFilterChange,
  onSelect
}: EmailConversationListProps) {
  const { t } = useTranslation();
  const tabs = useMemo(
    () => [
      { key: 'all' as const, label: t('email.filter_all') },
      { key: 'WAITING_CREATOR' as const, label: t('email.status_WAITING_CREATOR') },
      { key: 'NEED_REPLY' as const, label: t('email.status_NEED_REPLY') },
      { key: 'PROCESSED' as const, label: t('email.status_PROCESSED') },
      { key: 'HARD_BOUNCE' as const, label: t('email.status_HARD_BOUNCE') }
    ],
    [t]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-4 pb-2 pt-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-slate-400" />}
          placeholder={t('email.search_placeholder')}
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          className="rounded-lg"
        />
      </div>
      <div className="flex shrink-0 items-center gap-1 border-b border-[#eef0f4] px-3" role="tablist">
        {tabs.map((item) => {
          const active = item.key === filter;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`-mb-px inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap border-0 border-b-2 bg-transparent px-1.5 py-2 text-[13px] transition-colors ${
                active
                  ? 'border-[#596ae8] font-semibold text-[#3b4dd7]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => onFilterChange(item.key)}
            >
              {item.label}
              {item.key === 'NEED_REPLY' && pendingReplyCount != null ? (
                <span className="min-w-[16px] rounded-full bg-[#596ae8] px-1 text-center text-[10px] leading-4 text-white">
                  {pendingReplyCount > 99 ? '99+' : pendingReplyCount}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        className="chat-scroll min-h-0 flex-1 overflow-y-auto"
        onScroll={(event) => {
          if (!onLoadMore) return;
          const el = event.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) onLoadMore();
        }}
      >
        {loading && conversations.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spin size="small" />
          </div>
        ) : null}
        {!loading && conversations.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">{emptyText}</div>
        ) : null}
        {conversations.map((item) => {
          const active = item.id === activeId;
          const tag = emailStatusTag(item.status);
          return (
            <button
              key={item.id}
              type="button"
              className={`relative flex w-full cursor-pointer items-start gap-3 border-0 border-b border-[#f1f3f7] px-4 py-3 text-left transition-colors ${
                active ? 'bg-[#596ae8]/[0.07]' : 'bg-white hover:bg-slate-50'
              }`}
              onClick={() => onSelect(item.id)}
            >
              {active ? <span className="absolute inset-y-0 left-0 w-[3px] bg-[#596ae8]" aria-hidden /> : null}
              {item.avatarUrl ? (
                <img src={item.avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
              ) : (
                <ChatAvatar participant={item.peer} size={32} />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-sm font-semibold text-slate-800">{item.name}</span>
                    {tag ? (
                      <span className={`shrink-0 rounded px-1.5 text-[10px] leading-[18px] ${tag.className}`}>
                        {tag.label}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">{formatEmailTime(item.time)}</span>
                </span>
                <span
                  className={`mt-0.5 block truncate text-xs ${
                    item.unreadCount > 0 ? 'text-slate-600' : 'text-slate-400'
                  }`}
                >
                  {item.direction === 'OUTBOUND' ? t('chat.me_prefix') : ''}
                  {item.preview}
                </span>
              </span>
            </button>
          );
        })}
        {loadingMore ? (
          <div className="flex justify-center py-3">
            <Spin size="small" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
